import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { SUPPRESS_BAD_REQUEST_NOTIFICATION } from 'core';
import { CustomerDetailService, CustomerNotificationItem, DeliveryDestinations } from 'customers';
import { BankBranch } from 'receipts';
import { OrderBusinessType, OrderPaymentOption, OrderProposal, OrderSection } from '../interfaces/order-proposal.model';
import {
  OrderCustomerRequest,
  OrderDuplicateRequest,
  OrderOpenRequest,
  OrderSectionRequest,
} from '../interfaces/order-proposal-requests.model';
import { PagedResult } from '../interfaces/order-list.model';
import {
  CustomerCodesDialog,
  CustomerCodesDialogData,
  CustomerCodesDialogResult,
} from '../lib/select-customer/customer-codes-dialog/customer-codes-dialog';
import { sectionProposedCode } from '../utils/order.utils';

// Proposta d'ordine aperta: ogni scrittura risponde con la proposta intera, che sostituisce quella in memoria
@Injectable({ providedIn: 'root' })
export class OrderProposalService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly customerDetailService = inject(CustomerDetailService);

  private readonly _proposal = signal<OrderProposal | null>(null);
  readonly proposal = this._proposal.asReadonly();

  // Alert dell'ultima risposta, nell'header come nella scheda cliente
  private readonly _notifications = signal<CustomerNotificationItem[]>([]);

  // Anomalie del cliente della proposta
  readonly customerAlerts = computed(() => {
    const idPrimoCliente = this._proposal()?.idPrimoCliente;
    return idPrimoCliente
      ? this._notifications().filter(n => n.Category?.toLowerCase() === 'clienti' && n.CategoryId === idPrimoCliente)
      : [];
  });

  open(request: OrderOpenRequest): Observable<OrderProposal> {
    return this.write(this.http.post<OrderProposal>('api/ordini/apri', request, { observe: 'response' }));
  }

  // Nuova proposta copiata dall'ordine, con gli avvisi sulle righe cambiate
  duplicate(request: OrderDuplicateRequest): Observable<OrderProposal> {
    return this.write(this.http.post<OrderProposal>('api/ordini/duplica', request, { observe: 'response' }));
  }

  // Dimentica la proposta in memoria: la prossima apertura la rilegge dal backend
  clear(): void {
    this._proposal.set(null);
  }

  // Rilegge la proposta solo se in memoria ce n'è un'altra (es. ricarica della pagina)
  ensure(idOrdine: string): Observable<OrderProposal> {
    const current = this._proposal();
    return current?.id === idOrdine ? of(current) : this.open({ idOrdine });
  }

  exitWithoutSaving(): Observable<void> {
    return this.http.put<void>(`api/ordini/${this.id}/esciSenzaSalvare`, {}).pipe(
      tap(() => this._proposal.set(null)),
    );
  }

  // Senza un nome lo scrive il backend
  saveDraft(descrizione: string | null): Observable<OrderProposal> {
    return this.write(this.http.put<OrderProposal>(`api/ordini/${this.id}/bozza`, { descrizione }, { observe: 'response' }));
  }

  // Il 400 (blocco attivo o nessun codice valido) lo mostra il chiamante col motivo del backend
  setCustomer(idBusinessPartner: string): Observable<OrderProposal> {
    return this.write(this.http.put<OrderProposal>(`api/ordini/${this.id}/cliente`, { idBusinessPartner }, {
      observe: 'response',
      context: new HttpContext().set(SUPPRESS_BAD_REQUEST_NOTIFICATION, true),
    }));
  }

  // Foglio dei codici cliente: la scelta confermata si scrive subito, «Annulla» non cambia nulla
  chooseCustomerCodes(): Observable<OrderProposal | null> {
    const proposal = this._proposal()!;
    const lastUsed = [...proposal.oil.codiciCliente, ...proposal.nonOil.codiciCliente]
      .filter(code => code.ultimoUtilizzato)
      .map(code => String(code.codiceCliente));

    return this.customerDetailService.loadCustomerDetail(proposal.idBusinessPartner!).pipe(
      switchMap(({ detail }) => this.dialog
        .open<CustomerCodesDialog, CustomerCodesDialogData, CustomerCodesDialogResult>(CustomerCodesDialog, {
          width: '600px',
          maxWidth: '95vw',
          data: {
            ragioneSociale: proposal.ragioneSociale ?? '',
            oil: detail.oil ?? [],
            nonOil: detail.nonOil ?? [],
            initialOil: sectionProposedCode(proposal.oil),
            initialNonOil: sectionProposedCode(proposal.nonOil),
            lastUsed,
          },
        })
        .afterClosed()),
      switchMap(result => result ? this.setCustomerCodes(result.oilCode, result.nonOilCode) : of(null)),
    );
  }

  // I codici scelti nel foglio, con gli id della proposta
  private setCustomerCodes(oilCode: string | null, nonOilCode: string | null): Observable<OrderProposal> {
    const proposal = this._proposal()!;
    const request: OrderCustomerRequest = {
      idBusinessPartner: proposal.idBusinessPartner!,
      idClienteOil: this.codeId(proposal.oil, oilCode),
      idClienteNonOil: this.codeId(proposal.nonOil, nonOilCode),
    };
    return this.write(this.http.put<OrderProposal>(`api/ordini/${this.id}/cliente`, request, { observe: 'response' }));
  }

  // «Annulla» sui codici del cliente appena scelto: torna il cliente di prima, con destini e pagamenti, o nessun cliente
  restoreCustomer(previous: OrderProposal): Observable<OrderProposal> {
    if (!previous.idBusinessPartner) {
      return this.write(this.http.delete<OrderProposal>(`api/ordini/${previous.id}/cliente`, { observe: 'response' }));
    }

    const request: OrderCustomerRequest = {
      idBusinessPartner: previous.idBusinessPartner,
      idClienteOil: previous.oil.idCliente,
      idClienteNonOil: previous.nonOil.idCliente,
    };
    // Cambiando cliente destino e pagamento tornano gli abituali: si rimettono quelli della proposta
    return this.write(this.http.put<OrderProposal>(`api/ordini/${previous.id}/cliente`, request, { observe: 'response' })).pipe(
      switchMap(proposal => previous.oil.destino ? this.setSection('Oil', this.sectionRequest(previous.oil)) : of(proposal)),
      switchMap(proposal => previous.nonOil.destino ? this.setSection('NonOil', this.sectionRequest(previous.nonOil)) : of(proposal)),
    );
  }

  // Richiesta del nuovo cliente: dati in json e documenti come file, risponde con la proposta sul cliente provvisorio
  setNewCustomer(data: FormData): Observable<OrderProposal> {
    return this.write(this.http.post<OrderProposal>(`api/ordini/${this.id}/nuovocliente`, data, { observe: 'response' }));
  }

  getSectionDestinations(type: OrderBusinessType): Observable<DeliveryDestinations[]> {
    return this.http.get<DeliveryDestinations[]>(`api/ordini/${this.id}/sezioni/${type}/destini`);
  }

  getSectionPayments(type: OrderBusinessType): Observable<OrderPaymentOption[]> {
    return this.http.get<OrderPaymentOption[]>(`api/ordini/${this.id}/sezioni/${type}/pagamenti`);
  }

  // Salva la sezione com'è nella proposta con le modifiche indicate
  setSection(type: OrderBusinessType, changes: Partial<OrderSectionRequest>): Observable<OrderProposal> {
    const request: OrderSectionRequest = { ...this.sectionRequest(this.section(type)!), ...changes };
    return this.write(this.http.put<OrderProposal>(`api/ordini/${this.id}/sezioni/${type}`, request, { observe: 'response' }));
  }

  // Filiali per codice ABI o CAB, banca o agenzia, dal terzo carattere
  searchBankBranches(text: string): Observable<(BankBranch & { descrizione: string | null })[]> {
    if (text.trim().length < 3) {
      return of([]);
    }

    return this.bankBranches({ searchText: { text: text.trim() } }, 20);
  }

  // Banche per ABI o nome, una per ABI
  searchBanks(text: string): Observable<BankBranch[]> {
    if (!text.trim()) {
      return of([]);
    }

    return this.http.post<PagedResult<BankBranch>>(
      'api/lookup/filialibancarie/banche/list',
      { searchText: { text: text.trim() } },
      { params: { page: 1, pageSize: 20 } },
    ).pipe(map(result => result.items ?? []));
  }

  // CAB della banca scelta (tutti, filtrati da quanto scritto); senza banca solo sul CAB scritto
  searchCabs(abi: string | null, text: string): Observable<(BankBranch & { descrizione: string | null })[]> {
    if (!abi && !text.trim()) {
      return of([]);
    }

    return this.bankBranches({ searchObject: { abi, cab: text.trim() || null } }, 50);
  }

  private bankBranches(body: object, pageSize: number): Observable<(BankBranch & { descrizione: string | null })[]> {
    return this.http.post<PagedResult<BankBranch & { descrizione: string | null }>>(
      'api/lookup/filialibancarie/list',
      body,
      { params: { page: 1, pageSize } },
    ).pipe(map(result => result.items ?? []));
  }

  // Con «Resta aggiornato» prima chiede le notifiche
  send(notify: boolean): Observable<OrderProposal> {
    const notified$: Observable<unknown> = notify
      ? this.http.put<void>(`api/ordini/${this.id}/notifiche`, { attivo: true })
      : of(null);
    return notified$.pipe(
      switchMap(() => this.write(this.http.put<OrderProposal>(`api/ordini/${this.id}/invia`, {}, { observe: 'response' }))),
    );
  }

  generateQuote(): Observable<OrderProposal> {
    return this.write(this.http.put<OrderProposal>(`api/ordini/${this.id}/preventivo`, {}, { observe: 'response' }));
  }

  downloadQuote(idOrdine = this.id): Observable<{ blob: Blob; fileName: string }> {
    return this.http.get(`api/ordini/${idOrdine}/preventivo`, { observe: 'response', responseType: 'blob' }).pipe(
      map(response => ({
        blob: response.body as Blob,
        fileName: /filename=["']?([^;"']+)/i.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? 'preventivo.pdf',
      })),
    );
  }

  section(type: OrderBusinessType): OrderSection | null {
    const proposal = this._proposal();
    return proposal ? (type === 'Oil' ? proposal.oil : proposal.nonOil) : null;
  }

  private get id(): string {
    return this._proposal()!.id;
  }

  private sectionRequest(section: OrderSection): OrderSectionRequest {
    return {
      idDestino: section.destino?.id ?? null,
      idCodicePagamento: section.idCodicePagamento,
      abi: section.abi,
      cab: section.cab,
      // Il contatto della condizione sta nella nota del destino
      contatto: section.datoAggiuntivoPagamento === 'CONTATTO' ? section.destino?.nota?.contatto ?? null : null,
      dataSpedizione: section.dataSpedizione,
    };
  }

  private codeId(section: OrderSection, code: string | null): string | null {
    return section.codiciCliente.find(c => String(c.codiceCliente) === code)?.id ?? null;
  }

  private write(request$: Observable<HttpResponse<OrderProposal>>): Observable<OrderProposal> {
    return request$.pipe(
      tap(response => {
        const header = this.customerDetailService.parseNotificationHeader(response.headers.get('x-contextnotification'));
        if (header) {
          this._notifications.set(header.Notifications ?? []);
        }
        this._proposal.set(response.body);
      }),
      map(response => response.body as OrderProposal),
    );
  }
}
